import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Edit, Trash2, Clock, Plus } from "lucide-react";
import { format } from "date-fns";

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
}

interface TimeEntry {
  id: string;
  clock_in_time: string;
  clock_out_time: string | null;
  total_hours: number | null;
  notes: string | null;
}

interface EmployeeTimeTrackingProps {
  employee: Employee;
  timeEntries: TimeEntry[];
  onDataUpdate: () => void;
}

export const EmployeeTimeTracking: React.FC<EmployeeTimeTrackingProps> = ({
  employee,
  timeEntries,
  onDataUpdate
}) => {
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [addingEntry, setAddingEntry] = useState(false);
  const [loading, setLoading] = useState(false);

  const [entryForm, setEntryForm] = useState({
    clock_in_time: '',
    clock_out_time: '',
    notes: ''
  });

  const openEditDialog = (entry: TimeEntry) => {
    setEditingEntry(entry);
    setEntryForm({
      clock_in_time: format(new Date(entry.clock_in_time), "yyyy-MM-dd'T'HH:mm"),
      clock_out_time: entry.clock_out_time ? format(new Date(entry.clock_out_time), "yyyy-MM-dd'T'HH:mm") : '',
      notes: entry.notes || ''
    });
  };

  const openAddDialog = () => {
    setAddingEntry(true);
    setEntryForm({
      clock_in_time: '',
      clock_out_time: '',
      notes: ''
    });
  };

  const handleSaveEntry = async () => {
    setLoading(true);
    try {
      const clockInTime = new Date(entryForm.clock_in_time);
      const clockOutTime = entryForm.clock_out_time ? new Date(entryForm.clock_out_time) : null;
      
      // Calculate total hours if clock out time is provided
      let totalHours = null;
      if (clockOutTime) {
        totalHours = (clockOutTime.getTime() - clockInTime.getTime()) / (1000 * 60 * 60);
        if (totalHours <= 0) {
          toast.error('Clock out time must be after clock in time');
          return;
        }
      }

      const entryData = {
        employee_id: employee.id,
        clock_in_time: clockInTime.toISOString(),
        clock_out_time: clockOutTime?.toISOString() || null,
        total_hours: totalHours,
        notes: entryForm.notes || null
      };

      if (editingEntry) {
        // Update existing entry
        const { error } = await supabase
          .from('time_entries')
          .update(entryData)
          .eq('id', editingEntry.id);

        if (error) throw error;
        toast.success('Time entry updated successfully');
      } else {
        // Create new entry
        const { error } = await supabase
          .from('time_entries')
          .insert([entryData]);

        if (error) throw error;
        toast.success('Time entry added successfully');
      }

      setEditingEntry(null);
      setAddingEntry(false);
      onDataUpdate();
    } catch (error) {
      console.error('Error saving time entry:', error);
      toast.error('Failed to save time entry');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    try {
      const { error } = await supabase
        .from('time_entries')
        .delete()
        .eq('id', entryId);

      if (error) throw error;
      
      toast.success('Time entry deleted successfully');
      onDataUpdate();
    } catch (error) {
      console.error('Error deleting time entry:', error);
      toast.error('Failed to delete time entry');
    }
  };

  const calculateTotalHours = (entry: TimeEntry) => {
    if (entry.total_hours) return entry.total_hours;
    if (entry.clock_in_time && entry.clock_out_time) {
      const clockIn = new Date(entry.clock_in_time);
      const clockOut = new Date(entry.clock_out_time);
      return (clockOut.getTime() - clockIn.getTime()) / (1000 * 60 * 60);
    }
    return 0;
  };

  const totalHours = timeEntries.reduce((sum, entry) => sum + calculateTotalHours(entry), 0);

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Time Tracking Summary
            </CardTitle>
            <Button onClick={openAddDialog} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Entry
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold">{timeEntries.length}</p>
              <p className="text-sm text-muted-foreground">Total Entries</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{totalHours.toFixed(1)}h</p>
              <p className="text-sm text-muted-foreground">Total Hours</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{(totalHours / 30).toFixed(1)}h</p>
              <p className="text-sm text-muted-foreground">Daily Average</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Time Entries Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Time Entries</CardTitle>
        </CardHeader>
        <CardContent>
          {timeEntries.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No time entries found</p>
              <p className="text-sm text-muted-foreground mt-2">Add the first time entry to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Clock In</TableHead>
                  <TableHead>Clock Out</TableHead>
                  <TableHead className="text-right">Total Hours</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {timeEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      {format(new Date(entry.clock_in_time), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell>
                      {format(new Date(entry.clock_in_time), 'h:mm a')}
                    </TableCell>
                    <TableCell>
                      {entry.clock_out_time ? 
                        format(new Date(entry.clock_out_time), 'h:mm a') : 
                        <Badge variant="secondary">Still clocked in</Badge>
                      }
                    </TableCell>
                    <TableCell className="text-right">
                      {calculateTotalHours(entry).toFixed(2)}h
                    </TableCell>
                    <TableCell>{entry.notes || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(entry)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Time Entry</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this time entry? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDeleteEntry(entry.id)}
                                className="bg-destructive text-destructive-foreground"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit/Add Entry Dialog */}
      <Dialog open={!!editingEntry || addingEntry} onOpenChange={(open) => {
        if (!open) {
          setEditingEntry(null);
          setAddingEntry(false);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? 'Edit Time Entry' : 'Add Time Entry'}
            </DialogTitle>
            <DialogDescription>
              {editingEntry ? 'Update the time entry details' : 'Add a new time entry for this employee'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="clock_in_time">Clock In Time</Label>
              <Input
                id="clock_in_time"
                type="datetime-local"
                value={entryForm.clock_in_time}
                onChange={(e) => setEntryForm(prev => ({ ...prev, clock_in_time: e.target.value }))}
                required
              />
            </div>
            <div>
              <Label htmlFor="clock_out_time">Clock Out Time (Optional)</Label>
              <Input
                id="clock_out_time"
                type="datetime-local"
                value={entryForm.clock_out_time}
                onChange={(e) => setEntryForm(prev => ({ ...prev, clock_out_time: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={entryForm.notes}
                onChange={(e) => setEntryForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add any notes about this time entry..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setEditingEntry(null);
                setAddingEntry(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEntry} disabled={loading}>
              {loading ? 'Saving...' : 'Save Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};